import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  Clock,
  Edit,
  FileText,
  Users
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import { AppContext } from '../../../context/AppContext';
import { db } from '../../../firebase/config';
import { useCurrency } from '../../../utils/currencyUtils';
import { buildPayRunSummary, summarizePayslipStatus } from '../../../utils/payrollUtils';

const PayRunDetail = () => {
  const { payRunId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payRun, setPayRun] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      if (!activeBusiness) {
        setError('Select a business before viewing pay run details.');
        setLoading(false);
        return;
      }

      if (!payRunId) {
        setError('Pay run not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const payRunSnapshot = await getDoc(doc(db, 'payRuns', payRunId));
        if (!payRunSnapshot.exists()) {
          setError('Pay run not found');
          return;
        }

        const payRunData = payRunSnapshot.data();
        const normalizedPayRun = {
          id: payRunSnapshot.id,
          ...payRunData,
          startDate: payRunData.startDate?.toDate() || null,
          endDate: payRunData.endDate?.toDate() || null,
          paymentDate: payRunData.paymentDate?.toDate() || null
        };
        setPayRun(normalizedPayRun);

        const payrollSnapshot = await getDocs(
          query(collection(db, 'employeePayrollDetails'), where('businessId', '==', activeBusiness.id))
        );
        const payrollByEmployeeId = new Map(
          payrollSnapshot.docs.map((snapshot) => [snapshot.data().employeeId, { payrollId: snapshot.id, ...snapshot.data() }])
        );

        const selectedEmployeeIds = Array.isArray(payRunData.employeeIds) ? payRunData.employeeIds : [];
        const employeeRecords = await Promise.all(
          selectedEmployeeIds.map(async (employeeId) => {
            const payroll = payrollByEmployeeId.get(employeeId) || { employeeId };
            let employee = { id: employeeId, firstName: '', lastName: '', email: '', position: 'Worker' };

            const employeeSnapshot = await getDoc(doc(db, 'teamMembers', employeeId));
            if (employeeSnapshot.exists()) {
              const employeeData = employeeSnapshot.data();
              employee = {
                id: employeeSnapshot.id,
                firstName: employeeData.firstName || '',
                lastName: employeeData.lastName || '',
                email: employeeData.email || '',
                position: employeeData.position || 'Worker'
              };
            }

            return {
              ...payroll,
              employee,
              employmentType: payroll.employmentType || 'fullTime'
            };
          })
        );
        setEmployees(employeeRecords);

        const payslipSnapshot = await getDocs(query(collection(db, 'payslips'), where('payRunId', '==', payRunId)));
        const payslipRecords = payslipSnapshot.docs.map((snapshot) => {
          const data = snapshot.data();
          return {
            id: snapshot.id,
            ...data,
            paymentDate: data.paymentDate?.toDate ? data.paymentDate.toDate() : data.paymentDate,
            payPeriod: {
              startDate: data.payPeriod?.startDate?.toDate ? data.payPeriod.startDate.toDate() : data.payPeriod?.startDate,
              endDate: data.payPeriod?.endDate?.toDate ? data.payPeriod.endDate.toDate() : data.payPeriod?.endDate
            }
          };
        });
        setPayslips(payslipRecords);
      } catch (fetchError) {
        console.error('Error loading pay run detail:', fetchError);
        setError('Failed to load pay run details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeBusiness, currentUser, payRunId]);

  const getStatusClasses = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'processing') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatDate = (value) => {
    if (!value) return 'Not set';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleDateString();
  };

  const previewSummary = buildPayRunSummary(employees, payRun || {});
  const payslipStatus = summarizePayslipStatus(payslips, employees.length);
  const payslipByEmployeeId = new Map(payslips.map((payslip) => [payslip.employeeId, payslip]));

  const handleFinalize = async () => {
    if (payslips.length !== employees.length) {
      window.alert('Generate payslips for all selected workers before finalizing this pay run.');
      return;
    }

    try {
      setFinalizing(true);
      await updateDoc(doc(db, 'payRuns', payRunId), {
        status: 'completed',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setPayRun((current) => ({ ...current, status: 'completed' }));
    } catch (finalizeError) {
      console.error('Error finalizing pay run:', finalizeError);
      window.alert('Failed to finalize pay run. Please try again.');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error || !payRun) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <button
          onClick={() => navigate('/payroll/runs')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center mb-4"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Pay Runs
        </button>
        <div className="bg-red-50 rounded-lg p-6 text-center">
          <AlertCircle size={40} className="mx-auto text-red-500 mb-3" />
          <p className="text-red-800">{error || 'Pay run not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/payroll/runs')}
          className="text-[#190192] hover:text-[#2C0DB5] flex items-center"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Pay Runs
        </button>
        <h1 className="mt-2 text-xl font-bold text-gray-900">{payRun.name}</h1>
        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClasses(payRun.status)}`}>
            {payRun.status || 'draft'}
          </span>
          <span className="flex items-center">
            <Calendar size={14} className="mr-1" />
            {formatDate(payRun.startDate)} - {formatDate(payRun.endDate)}
          </span>
          <span className="flex items-center">
            <Clock size={14} className="mr-1" />
            Payment date: {formatDate(payRun.paymentDate)}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-gray-500">Workers</p>
              <p className="text-xl font-semibold text-gray-900">{previewSummary.employeeCount}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Gross Payroll</p>
              <p className="text-xl font-semibold text-gray-900">{formatAmount(previewSummary.totalGross)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Deductions</p>
              <p className="text-xl font-semibold text-gray-900">{formatAmount(previewSummary.totalDeductions)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Net Payroll</p>
              <p className="text-xl font-semibold text-[#190192]">{formatAmount(previewSummary.totalAmount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Payslips</p>
              <p className="text-xl font-semibold text-gray-900">
                {payslipStatus.payslipCount} / {employees.length}
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3 justify-end">
            {payRun.status === 'draft' && (
              <Link
                to={`/payroll/runs/${payRun.id}/edit`}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center"
              >
                <Edit size={16} className="mr-2" />
                Edit Pay Run
              </Link>
            )}

            <Link
              to={`/payroll/runs/${payRun.id}/payslips`}
              className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
            >
              <FileText size={16} className="mr-2" />
              {payslips.length > 0 ? 'Manage Payslips' : 'Generate Payslips'}
            </Link>

            {payRun.status !== 'completed' && (
              <button
                onClick={handleFinalize}
                disabled={finalizing}
                className={`px-4 py-2 rounded-lg text-white inline-flex items-center ${
                  finalizing ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <CheckCircle size={16} className="mr-2" />
                {finalizing ? 'Finalizing...' : 'Finalize Pay Run'}
              </button>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Worker payroll breakdown</h2>
          {employees.length === 0 ? (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
              <Users size={24} className="mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">No payroll workers are linked to this pay run.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Worker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Payslip</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewSummary.previews.map((preview) => {
                    const employeeRecord = employees.find((employee) => employee.employeeId === preview.employeeId);
                    const payslip = payslipByEmployeeId.get(preview.employeeId);

                    return (
                      <tr key={preview.employeeId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {employeeRecord?.employee?.firstName} {employeeRecord?.employee?.lastName}
                            </p>
                            <p className="text-sm text-gray-500">{employeeRecord?.employee?.position || 'Worker'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                          {preview.employmentType} - {preview.salaryType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatAmount(payslip?.grossAmount ?? preview.grossAmount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatAmount(payslip?.netAmount ?? preview.netAmount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              payslip ? (payslip.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800') : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {payslip ? payslip.status || 'draft' : 'pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {payslip ? (
                            <Link to={`/payroll/payslips/${payslip.id}`} className="text-[#190192] hover:text-[#2C0DB5]">
                              View Payslip
                            </Link>
                          ) : (
                            <Link to={`/payroll/runs/${payRun.id}/payslips`} className="text-[#190192] hover:text-[#2C0DB5]">
                              Generate
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {payRun.notes && (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
            <p className="text-gray-700">{payRun.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayRunDetail;
